/**
 * Right-anchored slide-over drawer, portalled to <body>.
 *
 * Reuses the modal backdrop but the surface is anchored right and
 * full-height, so the source row stays visible. Escape, backdrop and focus
 * behaviour live in `useDialog` (shared with `Modal`) — the drawer hosts the
 * question-answer textarea, so neither a stray Escape nor a drag-selection
 * ending outside the panel may discard an answer.
 */
import { type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";

import { useDialog } from "../../lib/hooks/useDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  eyebrow?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Optional Tailwind width override for the drawer surface. */
  widthClass?: string;
  initialFocusRef?: RefObject<HTMLElement | null>;
}

export function Drawer({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  widthClass = "w-[640px] max-w-full",
  initialFocusRef,
}: Props) {
  const { panelRef, titleId, backdropProps } = useDialog({
    open,
    onClose,
    kind: "drawer",
    initialFocusRef,
  });
  if (!open) return null;
  return createPortal(
    <div className="modal-backdrop" {...backdropProps}>
      <div
        ref={panelRef}
        className={clsx("admin-drawer", widthClass)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header flex items-start justify-between gap-4">
          <div>
            {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
            <h2 id={titleId}>{title}</h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            aria-label="Close drawer"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
